import "core-js/modules/es6.regexp.split";
import "core-js/modules/es6.number.constructor";
import _round from "lodash/round";

/** @module home-calculator-profile */
import transform from '@nerdwallet/transform'; // eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration

import { getCreditScoreRangeByValue, getCreditScoreRangeForCreditScore } from './rate-adjustment';
/**
 * Parses inputs to format accepted by create/updateProfile for identity HomeCalculatorProfile
 * @function
 * @param  {Object} inputs  Inputs to convert
 * @return {Object}
 */

export var toHomeCalculatorProfile = function toHomeCalculatorProfile(inputs) {
  return transform({
    purchase_price: 'purchasePrice',
    down_payment: 'downPayment',
    interest_rate_purchase: 'annualInterestRate',
    loan_term: 'loanTerm',
    income: 'income',
    monthly_debt: 'monthlyDebt',
    self_reported_credit_score: {
      value: function value() {
        if (inputs.creditScoreRangeValue) return getCreditScoreRangeByValue(inputs.creditScoreRangeValue).range[0];
        return undefined;
      }
    },
    refinance_goal: 'refinanceGoal',
    current_mortgage_amount: 'mortgageBalance',
    current_mortgage_interest_rate: 'currentMortgageInterestRate',
    current_mortgage_program: 'currentMortgageProgram',
    current_mortgage_originated_at: {
      value: function value() {
        // YYYY-MM-DD. Date has no meaning to the app so it is hardcoded to 5.
        // Months are zero indexed in javascript hence the offset
        if (inputs.originationYear) {
          return "".concat(inputs.originationYear, "-").concat(new Date().getMonth() + 1, "-5");
        }

        return undefined;
      }
    },
    current_mortgage_term: 'currentLoanTerm',
    new_mortgage_term: 'refinanceLoanTerm',
    new_mortgage_interest_rate: 'refinanceInterestRate',
    new_mortgage_cashout_amount: 'cashout',
    new_mortgage_closing_costs: 'newMortgageClosingCosts',
    has_completed_onboarding_hac: 'hasCompletedOnboardingHac',
    has_completed_onboarding_rc: 'hasCompletedOnboardingRc',
    monthly_rent: 'monthlyRent',
    property_tax_rate: 'propertyTaxRate',
    monthly_hoa: 'HOADues',
    homeowners_insurance: {
      value: function value() {
        // This should be fixed at the db level but hacking it for now
        if (inputs.homeInsurance) return _round(inputs.homeInsurance * 10000);
        return undefined;
      }
    },
    home_repairs: 'maintainanceFees',
    home_renovations: 'renovationFees',
    income_tax_rate: 'incomeTaxRate',
    renters_insurance: 'rentersInsurance',
    cost_clothing: 'buyingCosts',
    closing_costs: 'sellingCosts',
    rent_security_deposit: 'rentSecurityDeposit',
    home_value_appreciation: 'homeAppreciation',
    rent_appreciation: 'rentAppreciation',
    general_inflation: 'generalInflation',
    annual_return_cash: {
      value: function value() {
        // This should be fixed at the db level but hacking it for now
        if (inputs.annualReturnCash) return _round(inputs.annualReturnCash * 10000);
        return undefined;
      }
    },
    capital_gains_tax_rate: 'capitalGainsTaxRate',
    tax_filing_status: 'taxFilingStatus',
    'purchase_address.city': 'purchaseAddress.city',
    'purchase_address.state': 'purchaseAddress.state',
    'purchase_address.zip_code': 'purchaseAddress.zip',
    'refinance_address.city': 'refinanceAddress.city',
    'refinance_address.state': 'refinanceAddress.state',
    'refinance_address.zip_code': 'refinanceAddress.zip'
  }, inputs);
};
/**
 * Parses HomeCalculatorProfile from identity to a format accepted by inputs from home-app-lib
 * @function
 * @param  {CalculatorProfile} calculatorProfile The profile to parse
 * @return {Object}
 */

export var fromHomeCalculatorProfile = function fromHomeCalculatorProfile(calculatorProfile) {
  return transform({
    purchasePrice: 'purchase_price',
    downPayment: 'down_payment',
    annualInterestRate: 'interest_rate_purchase',
    loanTerm: 'loan_term',
    income: 'income',
    monthlyDebt: 'monthly_debt',
    creditScoreRangeValue: {
      value: function value() {
        if (calculatorProfile && calculatorProfile.self_reported_credit_score) return getCreditScoreRangeForCreditScore(calculatorProfile.self_reported_credit_score).value;
        return undefined;
      }
    },
    refinanceGoal: 'refinance_goal',
    mortgageBalance: 'current_mortgage_amount',
    currentMortgageInterestRate: 'current_mortgage_interest_rate',
    currentMortgageProgram: 'current_mortgage_program',
    currentLoanTerm: 'current_mortgage_term',
    refinanceLoanTerm: 'new_mortgage_term',
    refinanceInterestRate: 'new_mortgage_interest_rate',
    cashout: 'new_mortgage_cashout_amount',
    newMortgageClosingCosts: 'new_mortgage_closing_costs',
    hasCompletedOnboardingHac: 'has_completed_onboarding_hac',
    hasCompletedOnboardingRc: 'has_completed_onboarding_rc',
    monthlyRent: 'monthly_rent',
    propertyTaxRate: 'property_tax_rate',
    HOADues: 'monthly_hoa',
    homeInsurance: {
      value: function value() {
        // This should be fixed at the db level say we save 5.25%
        if (calculatorProfile && calculatorProfile.homeowners_insurance) return _round(calculatorProfile.homeowners_insurance / 10000, 4);
        return undefined;
      }
    },
    maintainanceFees: 'home_repairs',
    renovationFees: 'home_renovations',
    incomeTaxRate: 'income_tax_rate',
    rentersInsurance: 'renters_insurance',
    buyingCosts: 'cost_clothing',
    sellingCosts: 'closing_costs',
    originationMonth: {
      value: function value() {
        if (calculatorProfile && calculatorProfile.current_mortgage_originated_at) {
          return Number(calculatorProfile.current_mortgage_originated_at.split('-')[1]);
        }

        return undefined;
      }
    },
    originationYear: {
      value: function value() {
        if (calculatorProfile && calculatorProfile.current_mortgage_originated_at) {
          return Number(calculatorProfile.current_mortgage_originated_at.split('-')[0]);
        }

        return undefined;
      }
    },
    rentSecurityDeposit: 'rent_security_deposit',
    homeAppreciation: 'home_value_appreciation',
    rentAppreciation: 'rent_appreciation',
    generalInflation: 'general_inflation',
    annualReturnCash: {
      value: function value() {
        // This should be fixed at the db level say we save 5.25%
        if (calculatorProfile && calculatorProfile.annual_return_cash) return _round(calculatorProfile.annual_return_cash / 10000, 4);
        return undefined;
      }
    },
    taxFilingStatus: 'tax_filing_status',
    capitalGainsTaxRate: 'capital_gains_tax_rate',
    'purchaseAddress.city': 'purchase_address.city',
    'purchaseAddress.state': 'purchase_address.state',
    'purchaseAddress.zip': 'purchase_address.zip',
    'refinanceAddress.city': 'refinance_address.city',
    'refinanceAddress.state': 'refinance_address.state',
    'refinanceAddress.zip': 'refinance_address.zip'
  }, calculatorProfile);
};